"use client";
import PropCard from "../prop-card";
import Container from "@/components-shared/container";
import { H2, H3 } from "@/components-shared/headers";
import { Button } from "@/components-shared/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselNext,
  CarouselPrevious,
  CarouselItem,
} from "@/components-shared/ui/carousel";
import React, { Fragment, useEffect } from "react";
import PropCardSkeleton from "../prop-card-skeleton";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { dir } from "i18next";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { useIntersectionObserver } from "usehooks-ts";
import { useGetInfiniteProps } from "@/src/app/all-units/utils/get-props-hook";

const AvailableUnits = () => {
  const locale = useLocale();
  const t = useTranslations();
  const { ref, isIntersecting } = useIntersectionObserver({ threshold: 0 });
  const { isLoggedIn } = useSelector((state: RootState) => state.login);
  const {
    data,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetInfiniteProps();

  useEffect(() => {
    refetch();
  }, [isLoggedIn, refetch]);

  useEffect(() => {
    if (isIntersecting && !isFetchingNextPage) fetchNextPage();
  }, [isIntersecting]);

  return (
    <Container className="mt-10 sm:mt-16 mb-5">
      <div className="flex justify-between items-center max-sm:flex-wrap">
        <div className="space-y-3">
          <H3 className="text-green-main font-semibold">{t("units.title")}</H3>
        </div>
        <Button asChild size={"sm"}>
          <Link href={{ pathname: "/all-units", query: { page: 1 } }}>
            {t("offers.viewAllUnits")}
          </Link>
        </Button>
      </div>

      {error ||
      (!data?.pages.flatMap((p) => p.properties)?.length && !isLoading) ? (
        <H2 className="flex items-center justify-center h-64 text-gray-light">
          {t("noData")}
        </H2>
      ) : (
        <Carousel
          opts={{
            direction: dir(locale as string),
            dragFree: true,
          }}
        >
          <CarouselContent className="py-5 sm:py-10 px-2 sm:px-4">
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <CarouselItem key={`card ${i}`} className="max-w-xs">
                    <PropCardSkeleton />
                  </CarouselItem>
                ))
              : data?.pages.map((page) => (
                  <Fragment key={page.page}>
                    {page?.properties?.map((prop: Property, i: number) => (
                      <CarouselItem key={`card ${i}`} className="max-w-xs">
                        <PropCard
                          propData={prop}
                          image={prop?.media?.find((m) => m.isMain)?.url}
                          id={prop.id}
                        />
                      </CarouselItem>
                    ))}
                  </Fragment>
                ))}
            {hasNextPage && (
              <CarouselItem ref={ref} className="max-w-xs h-full">
                <PropCardSkeleton />
              </CarouselItem>
            )}
          </CarouselContent>
          <div className="flex gap-4 sm:gap-8 justify-end">
            <CarouselPrevious className="rtl:rotate-180 " />
            <CarouselNext className="rtl:rotate-180" />
          </div>
        </Carousel>
      )}
    </Container>
  );
};

export default AvailableUnits;
